/**
 * Timing and protocol constants shared by the relay and the web client.
 *
 * The 5-minute rule: once a message (or call-log entry) has been *delivered* to
 * the recipient's client, both clients hard-delete it after DELIVERED_TTL_MS.
 * The relay stores an *undelivered* message only until GRACE_TTL_MS, then purges
 * it whether or not it was ever picked up.
 */
/** How long a delivered message / call-log entry survives on a client. */
export const DELIVERED_TTL_MS = 5 * 60 * 1000;
/**
 * Default upper bound on how long the relay keeps an *undelivered* ciphertext
 * envelope. This is the only content-bearing data that outlives 5 minutes, and
 * it is opaque (E2E) ciphertext the relay cannot read. Operator-configurable.
 */
export const DEFAULT_GRACE_TTL_MS = 24 * 60 * 60 * 1000;
/** Lower / upper bounds the relay will accept for its configured grace window. */
export const MIN_GRACE_TTL_MS = 60 * 1000;
export const MAX_GRACE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Client sweeper cadence while a tab / service worker is alive. */
export const SWEEP_INTERVAL_MS = 30 * 1000;
/** Relay purge-job cadence for expired undelivered envelopes. */
export const PURGE_INTERVAL_MS = 60 * 1000;
/** Invite links / QR tokens expire this long after creation. */
export const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** How many one-time MLS KeyPackages a client keeps published on the relay. */
export const KEY_PACKAGE_TARGET = 20;
export const KEY_PACKAGE_MIN = 5;
/** Max accepted sizes (bytes, pre-base64) — defense against a hostile client. */
export const MAX_CIPHERTEXT_BYTES = 256 * 1024;
export const MAX_KEY_PACKAGE_BYTES = 16 * 1024;
export const MAX_USERNAME_LEN = 32;
export const MIN_USERNAME_LEN = 3;
/**
 * Attachments (images/video/PDF/etc.) travel a separate path from the normal
 * 256KB-capped message channel — see server/src/routes/attachments.ts. The
 * relay only ever holds ciphertext it can't decrypt, same as pending_messages.
 */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/** How long an uploaded attachment blob survives on the relay if never fetched. */
export const ATTACHMENT_GRACE_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * How long the relay holds call signaling (invite, ICE candidates, end) for a
 * callee who isn't connected right now — long enough to tap the "Incoming
 * call" notification and open the app, short enough that a ring can never
 * show up long after the caller gave up. Also the caller's no-answer timeout.
 */
export const CALL_RING_TTL_MS = 60 * 1000;
/**
 * If the recipient's socket hasn't ACKed a delivered envelope within this long,
 * assume the app is backgrounded (iOS suspends a web app's socket without
 * closing it) and send a push notification.
 */
export const PUSH_ACK_WAIT_MS = 3 * 1000;
/** Most user ids one connection may watch for online status (a `watch` frame). */
export const MAX_PRESENCE_WATCH = 500;
/** Current wire-protocol version. Bumped on any breaking envelope change. */
export const WIRE_VERSION = 1;
/** WebSocket close codes used by the relay. */
export const CLOSE_CODES = {
    UNAUTHENTICATED: 4001,
    AUTH_TIMEOUT: 4002,
    BAD_MESSAGE: 4003,
    REPLACED_BY_NEWER_SESSION: 4004,
    RATE_LIMITED: 4008,
};
//# sourceMappingURL=constants.js.map